import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard, redirectUnauthorizedTo } from '@angular/fire/auth-guard';
import { RedirectComponent } from './redirect.component';
import { PurchaseResolver } from './core/resolvers/purchase.resolver';
import { LayoutComponent } from './layout/layout.component';
import { AccountDetailsLayoutComponent } from './accounts/account-details-layout/account-details-layout.component';
import { CompanySettingsResolver } from './core/resolvers/company-settings.resolver';
import { RelatedUsersResolver } from './core/resolvers/related-users.resolver';

import { DealDetailsLayoutComponent } from './deals/deal-details/deal-details-layout/deal-details-layout.component';
import { PermissionsGuard } from './core/permissions.guard';
import { EquipmentDetailsLayoutComponent } from './equipment/equipment-details/equipment-details-layout/equipment-details-layout.component';
import { LocationComponent } from './location/location/location.component';

const redirectUnauthorizedToLogin = () => redirectUnauthorizedTo([ 'auth/sign-in' ]);
const routes: Routes = [
  {
    path: '',
    redirectTo: '/auth/sign-in',
    pathMatch: 'full',
  },
  {
    path: 'redirect',
    component: RedirectComponent,
  },
  {
    path: 'ui-style',
    loadChildren: () => import('./ui-style/ui-style.routes').then(m => m.uiStyleRoutes),
  },
  {
    path: 'auth',
    loadChildren: () => import('./auth/auth.module').then(m => m.AuthModule),
  },
  {
    path: '',
    component: LayoutComponent,
    canActivate: [
      AuthGuard,
    ],
    data: {
      authGuardPipe: redirectUnauthorizedToLogin,
    },
    resolve: {
      companySettings: CompanySettingsResolver,
      purchase: PurchaseResolver,
      relatedUsersResolver: RelatedUsersResolver,
    },
    children: [
      {
        path: 'dashboard',
        loadChildren: () => import('./dashboard/dashboard.routes').then(m => m.dashboardRoutes),
      },
      {
        path: 'users',
        loadChildren: () => import('./users/users.module').then(m => m.UsersModule),
      },
      {
        path: 'accounts/:accountId',
        component: AccountDetailsLayoutComponent,
        children: [
          {
            path: 'details',
            loadChildren: () => import('./accounts/account-details/account-details.module').then(m => m.AccountDetailsModule),
          },
          {
            path: 'contacts',
            loadComponent: () => import('./accounts/account-contacts/account-contacts.component').then(m => m.AccountContactsComponent),
          },
          {
            path: 'tasks',
            loadChildren: () => import('./accounts/account-tasks/account-tasks.module').then(m => m.AccountTasksModule),
          },
          {
            path: 'shared',
            loadChildren: () => import('./accounts/shared-accounts/shared-accounts.module').then(m => m.SharedAccountsModule),
          },
          {
            path: 'account-activity-logs',
            loadChildren: () => import('./accounts/account-activity-logs/account-activity-logs.module').then(m => m.AccountActivityLogsModule),
          },
          {
            path: 'daily-routes',
            loadChildren: () => import('./accounts/account-daily-routes/account-daily-routes.module').then(m => m.AccountDailyRoutesModule),
          },
          {
            path: 'children',
            loadComponent: () => import('./accounts/account-children/account-children.component').then(m => m.AccountChildrenComponent),
          },
          {
            path: 'documents',
            loadComponent: () => import('./accounts/account-documents/account-documents.component').then(c => c.AccountDocumentsComponent),
          },
          {
            path: 'deals',
            loadComponent: () => import('./accounts/account-deals/account-deals.component').then(c => c.AccountDealsComponent),
          },
          {
            path: 'equipment',
            loadComponent: () => import('./accounts/account-equipment/account-equipment.component').then(c => c.AccountEquipmentComponent),
          },
        ]
      },
      {
        path: 'accounts',
        loadChildren: () => import('./accounts/accounts.module').then(m => m.AccountsModule),
      },
      {
        path: 'location',
        component: LocationComponent,
      },
      {
        path: 'documents',
        loadChildren: () => import('./documents/documents.routes').then(m => m.documentsRoutes),
      },
      {
        path: 'settings',
        loadChildren: () => import('./settings/settings.module').then(m => m.SettingsModule),
      },
      {
        path: 'reports/adhoc',
        loadChildren: () => import('./reports/adhoc-report/adhoc-report.routes').then(m => m.adhocReportRoutes),
      },
      {
        path: 'reports/accounts',
        redirectTo: 'reports/adhoc',
        pathMatch: 'full',
      },
      {
        path: 'reports/activity',
        loadChildren: () => import('./reports/activity-report/activity-report.routes').then(m => m.activityReportRoutes),
      },
      {
        path: 'reports/daily-route',
        loadChildren: () => import('./reports/daily-route-report/daily-route-report.routes').then(m => m.dailyRouteReportRoutes),
      },
      {
        path: 'deals',
        loadComponent: () => import('./deals/deals-page/deals-page.component').then(m => m.DealsPageComponent),
      },
      {
        path: 'deals/settings',
        canMatch: [ PermissionsGuard ],
        data: {
          permissions: {
            redirectTo: 'dashboard',
            only: [ 'OWNER' ],
            message: 'Failed to access deals settings',
          },
        },
        loadComponent: () => import('./deals/deal-pipelines/deal-pipelines.component').then(m => m.DealPipelinesComponent),
      },
      {
        path: 'deals/settings/:pipelineId',
        canMatch: [ PermissionsGuard ],
        data: {
          permissions: {
            redirectTo: 'dashboard',
            only: [ 'OWNER' ],
            message: 'Failed to access deals settings',
          },
        },
        loadComponent: () => import('./deals/deal-stages/deal-stages.component').then(m => m.DealStagesComponent),
      },
      {
        path: 'deals/:id',
        component: DealDetailsLayoutComponent,
        children: [
          {
            path: 'details',
            loadComponent: () => import('./deals/deal-details/deal-details.component').then(m => m.DealDetailsComponent),
          },
          {
            path: 'tasks',
            loadComponent: () => import('./deals/deal-details/deal-tasks/deal-tasks.component').then(m => m.DealTasksComponent),
          },
        ],
      },
      {
        path: 'equipment',
        loadComponent: () => import('./equipment/equipment-page.component').then(m => m.EquipmentPageComponent),
      },
      {
        path: 'equipment/:id',
        component: EquipmentDetailsLayoutComponent,
        children: [
          {
            path: 'details',
            loadComponent: () => import('./equipment/equipment-details/equipment-details.component').then(m => m.EquipmentDetailsComponent),
          },
          {
            path: 'wo',
            loadComponent: () => import('./equipment/equipment-details/external-wo-list/external-wo-list.component').then(m => m.ExternalWoListComponent),
          },
          {
            path: 'photos',
            loadComponent: () => import('./equipment/equipment-details/equipment-photos/equipment-photos.component').then(m => m.EquipmentPhotosComponent),
          },
        ],
      },
    ]
  },
];

@NgModule({
  imports: [ RouterModule.forRoot(routes) ],
  exports: [ RouterModule ]
})
export class AppRoutingModule {
}
